import React, { useCallback, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { ListChecks } from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Hover/click pill showing a linear, pricing-free tally of every SKU+flavour in
// the whole order: saved savers + loose cart items (+ optionally the saver
// currently being built, passed via extraItems — one entry per tub).
// If `cart` is provided the component uses it directly; otherwise it fetches
// GET /cart?customerId= fresh each time the popover opens.
const OrderTotalsPopover = ({ customerId, cart: cartProp = null, extraItems = [] }) => {
  const [open, setOpen] = useState(false);
  const [fetchedCart, setFetchedCart] = useState(null);
  const [loading, setLoading] = useState(false);
  const closeTimer = useRef(null);

  const cart = cartProp || fetchedCart;

  const fetchCart = useCallback(() => {
    if (cartProp || !customerId) return;
    setLoading(true);
    axios.get(`${API}/cart`, { params: { customerId } })
      .then((res) => setFetchedCart(res.data))
      .catch(() => setFetchedCart(null))
      .finally(() => setLoading(false));
  }, [cartProp, customerId]);

  const show = () => {
    clearTimeout(closeTimer.current);
    if (!open) {
      fetchCart();
      setOpen(true);
    }
  };
  const hide = () => {
    closeTimer.current = setTimeout(() => setOpen(false), 250);
  };

  const rows = useMemo(() => {
    const agg = new Map();
    const add = (name, size, flavour, qty, current = false) => {
      if (!name || !qty) return;
      const label = `${name}${size ? ` ${size}` : ''}${flavour ? ` — ${flavour}` : ''}`;
      const entry = agg.get(label) || { label, qty: 0, current: false };
      entry.qty += qty;
      entry.current = entry.current || current;
      agg.set(label, entry);
    };
    (cart?.savers || []).forEach((b) =>
      (b.products || []).forEach((p) => add(p.name, p.size, p.selectedFlavour, 1)));
    (cart?.looseItems || []).forEach((it) =>
      add(it.productName, it.size, it.selectedFlavour, Number(it.qty) || 0));
    extraItems.forEach((p) => add(p.name, p.size, p.selectedFlavour, 1, true));
    return [...agg.values()].sort((a, b) => a.label.localeCompare(b.label));
  }, [cart, extraItems]);

  const totalUnits = rows.reduce((s, r) => s + r.qty, 0);
  const hasCurrent = extraItems.length > 0;

  if (!customerId) return null;

  return (
    <div className="relative inline-block" onMouseEnter={show} onMouseLeave={hide}>
      <button
        type="button"
        onClick={() => (open ? setOpen(false) : show())}
        data-testid="order-totals-button"
        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs border border-border-color rounded-full text-text-secondary hover:border-gold hover:text-gold transition-colors"
        title="See how many of each item are in the whole order"
      >
        <ListChecks size={14} weight="bold" /> Order totals
      </button>
      {open && (
        <div
          data-testid="order-totals-popover"
          className="absolute right-0 top-full mt-2 w-80 max-h-96 overflow-y-auto bg-white border border-gray-300 rounded-xl shadow-2xl z-40 p-4"
        >
          <p className="text-[11px] uppercase tracking-wide font-bold text-amber-700 mb-2">Everything in this order</p>
          {loading && !cart && <p className="text-xs text-gray-500 italic">Loading…</p>}
          {!loading && rows.length === 0 && (
            <p className="text-xs text-gray-500 italic" data-testid="order-totals-empty">
              Nothing in the order yet.
            </p>
          )}
          {rows.length > 0 && (
            <>
              <ul className="space-y-1.5">
                {rows.map((r) => (
                  <li key={r.label} className="flex items-baseline justify-between gap-3 text-xs">
                    <span className="text-gray-900 leading-snug">
                      {r.label}
                      {r.current && (
                        <span className="text-amber-600 ml-1" title="Includes tubs from the saver you're building now">•</span>
                      )}
                    </span>
                    <span className="font-bold text-gray-900 shrink-0">× {r.qty}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-3 pt-2 border-t border-gray-300 flex justify-between text-xs">
                <span className="text-gray-500">
                  {hasCurrent ? 'Total items (incl. current saver •)' : 'Total items'}
                </span>
                <span className="font-bold text-amber-700" data-testid="order-totals-total">{totalUnits}</span>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default OrderTotalsPopover;
